import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { enqueueSnackbar } from "notistack";
import { RHFTextField } from "../../hook-form/RHFTextField";
import { useElImageFetch } from "../../../hooks/useElImageFetch";
import { Stack, Button } from "@mui/material";
import { FormValues } from "../../../types/elViewerTypes";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const elImageSchema: yup.ObjectSchema<FormValues> = yup
  .object({
    serialNumber: yup.string().trim().optional(),
    trackingNumber: yup.string().trim().optional(),
  })
  .test(
    "xor",
    "Please fill either Serial Number or Tracking Number (but not both)",
    (values): boolean => {
      const hasSerial = !!values?.serialNumber?.trim();
      const hasTracking = !!values?.trackingNumber?.trim();
      return (hasSerial || hasTracking) && !(hasSerial && hasTracking);
    }
  );

export function ElImageForm({
  setImageUrl,
  setOpen,
}: {
  setImageUrl: (imageUrl: string) => void;
  setOpen: (value: boolean) => void;
}) {
  const methods = useForm<FormValues>({
    defaultValues: { serialNumber: "", trackingNumber: "" },
    resolver: yupResolver(elImageSchema),
  });

  const { fetchElImage, loading } = useElImageFetch();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const { url, error } = await fetchElImage(data);

    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      return;
    }

    if (url) {
      setImageUrl(url);
      setOpen(true);
      methods.reset();
      enqueueSnackbar("Image loaded successfully.", { variant: "success" });
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit, (errors) => {
          if (Object.keys(errors).length > 0) {
            enqueueSnackbar(
              "Validation failed: Please fill exactly one field",
              { variant: "error" }
            );
          }
        })}
      >
        <Stack spacing={2}>
          <RHFTextField name="serialNumber" label="Serial Number" />
          <RHFTextField name="trackingNumber" label="Tracking Number" />
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? "Loading..." : "Submit"}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
}
